/**
 * axios setup to use mock service
 */

import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

const axiosServices = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/' });
import { GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE, translateError } from './error-translator';

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getIsLoggingOut } from 'contexts/FirebaseContext';

axiosServices.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        const token = await user.getIdToken();
        localStorage.setItem('serviceToken', token);
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        const accessToken = localStorage.getItem('serviceToken');
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      }
    } catch (e) {
      console.error('Error getting token', e);
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

/**
 * Pending audit 2026-10-30 (WEB-1): a request made with `responseType: 'blob'` (the invoice PDF,
 * features/account/api.ts) gets its error body as a Blob too, with the backend's JSON
 * `{code, message}` inside — unread, the invoice codes never reached the user. Read it back when
 * it is JSON; anything else (a real binary, an unreadable Blob) is `null`, i.e. "no message".
 */
async function readErrorBody(data: unknown): Promise<unknown> {
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return data;
  try {
    const text = typeof data.text === 'function' ? await data.text() : await readBlobAsText(data);
    const looksLikeJson = /json/i.test(data.type) || text.trimStart().startsWith('{');
    return looksLikeJson ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

axiosServices.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (getIsLoggingOut()) {
        return Promise.reject(error);
      }
      if (!window.location.href.includes('/login')) {
        window.location.pathname = '/login';
      }
    }

    // Pending audit 2026-10-30 (WEB-2): no answer at all (network down, CORS, timeout). Nothing to
    // translate, and `response` stays undefined on purpose: callers that key their own fallback
    // off a missing `response.data.message` (the invoice download's 'No se pudo descargar el PDF')
    // must keep seeing it missing.
    if (!error.response) {
      error.message = NETWORK_ERROR_MESSAGE;
      return Promise.reject(error);
    }

    // NEX-1371: the backend now always answers errors as {success:false, code, message}.
    // This used to reject with a spread plain object (or a bare string), which stripped
    // `.response` from the rejected value — every caller reading the standard axios shape
    // (`err.response?.data?.message`/`.code`, ~40+ call sites across the app) silently got
    // `undefined` and fell back to a generic message. Rejecting with the real error object
    // instead keeps `.response.data` intact, while still exposing the translated message
    // (and any extra fields like `code`/`currentAmount`/`expectedAmount` for PRICE_CHANGED)
    // directly on the error too, for the call sites that read it that way.
    const originalData: unknown = (await readErrorBody(error.response.data)) || null;
    const isObject = typeof originalData === 'object' && originalData !== null;
    // `dataObject` is a typing convenience only — same object, same runtime shape as
    // `originalData` when `isObject` is true. Property reads below stay exactly as loose as
    // the original (`.message`/`.code` truthy-checked, not `typeof`-narrowed to string): the
    // real backend contract (NEX-1371) always sends strings for both, but nothing here should
    // start rejecting a differently-shaped payload that the pre-TS code would have accepted.
    const dataObject = isObject ? (originalData as Record<string, unknown>) : null;
    // Pending audit 2026-10-30 (WEB-1/WEB-2): a body with no `message` — an envelope without text,
    // a Blob that was not JSON, a proxy's HTML error page (kept out: markup is not a message) —
    // used to end as the template's literal 'Wrong Services', shown as-is by ~10 screens.
    const bodyText = typeof originalData === 'string' && !originalData.trimStart().startsWith('<') ? originalData : '';
    const rawMessage: unknown = dataObject && dataObject.message ? dataObject.message : bodyText || GENERIC_ERROR_MESSAGE;
    const code = dataObject ? (dataObject.code as string | undefined) : undefined;
    const translatedMessage = translateError(rawMessage, code);

    if (dataObject) {
      Object.assign(error, dataObject, { message: translatedMessage });
      error.response.data = { ...dataObject, message: translatedMessage };
    } else {
      // Error.message is declared `string` in lib.es5.d.ts; translatedMessage is only ever
      // non-string if `rawMessage` itself was (see the comment above) — same as the pre-TS
      // code, which happily assigned whatever fell out of this chain with no type at all.
      error.message = translatedMessage as string;
    }

    return Promise.reject(error);
  }
);

export default axiosServices;

export async function fetcher<T = unknown>(args: string | [string, AxiosRequestConfig?]): Promise<T> {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get<T>(url, { ...config });

  return res.data;
}
